import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import {
  listTalentActions,
  setTalentAction,
  type ListActionsParams,
  type SetActionParams,
  type ListActionsResponse,
} from './talentActionsApi';

export const TALENT_ACTIONS_QUERY_KEY = ['talent', 'actions'] as const;

export function useTalentActions(params: ListActionsParams) {
  return useQuery<ListActionsResponse>({
    queryKey: [
      ...TALENT_ACTIONS_QUERY_KEY,
      params.shortlistPublicKey,
      params.talentPublicKey,
    ],
    queryFn: () => listTalentActions(params),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 404 || error.status === 403)) return false;
      return failureCount < 2;
    },
  });
}

export function useSetTalentAction(params: ListActionsParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<SetActionParams, 'type'>) =>
      setTalentAction({
        shortlistPublicKey: params.shortlistPublicKey,
        talentPublicKey: params.talentPublicKey,
        type: input.type,
      }),
    onSuccess: async () => {
      await Promise.all([
        // SW-145: legacy key (TalentActionsPanel y TalentDetailPage siguen usando
        // `useTalentActions` para mostrar el historial completo de acciones del
        // cliente sobre el talent).
        qc.invalidateQueries({
          queryKey: [
            ...TALENT_ACTIONS_QUERY_KEY,
            params.shortlistPublicKey,
            params.talentPublicKey,
          ],
        }),
        // SW-145: el list response trae `myAction` per talent (eliminó el N+1).
        // Después de una mutation, invalidamos el list para refetchar con el
        // nuevo estado y que el card refleje el cambio sin per-card query.
        qc.invalidateQueries({
          queryKey: ['shortlists', 'talents', params.shortlistPublicKey],
        }),
      ]);
    },
  });
}
